import React from "react";
import { withTranslation } from "react-i18next";
import _ from "lodash";
import moment from "moment";
import submit from "submit-form";
import { DragDropProvider } from "@dnd-kit/react";

import MonthGrid from "./components/MonthGrid";

import MonthWeek from "./components/MonthWeek";

import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import CalendarDuplicateWorkoutModal from "../../../parts/modals/calendar-duplicate-workout";
import CalendarMoveWorkoutModal from "../../../parts/modals/calendar-move-workout";
import CalendarAddExerciseModal from "../../../parts/modals/calendar-add-exercise";
import CalendarShiftWorkoutModal from "../../../parts/modals/calendar-shift-workout";
import VideoModal from "../../../parts/modals/video";

import SvgIcon from "../../../parts/SvgIcon";
import { RillContext } from "../../../rill/RillReact/share/base";
import {
  getCalendarMonthGridBounds,
  getCalendarMonthInsightsRange,
} from "../../athlete-portal/programs/program-insights/utils/inputs/calendarInsights";
import CalendarInsightsSection from "../../athlete-portal/calendar/components/CalendarInsightsSection";

const {
  DATE_FORMATS: { DATE_YEAR_FORMAT, DATE_DAY_FORMAT, UTC },
} = require("../../../constants");

const weeksInMonth = [...Array(6)].map((_, i) => i);

class View extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      showFooter: true,
      selectedDates: [],
      loaded: false,
      scheduledSessions: context.locals.scheduledSessions,
      expanded: true,
    };

    this.handleSelectDate = this.handleSelectDate.bind(this);
    this.handleRemoveMultiple = this.handleRemoveMultiple.bind(this);
    this.handleDeselectAll = this.handleDeselectAll.bind(this);
    this.handleSelectAll = this.handleSelectAll.bind(this);

    this.goToMyPrograms = this.goToMyPrograms.bind(this);
    this.goToContactUs = this.goToContactUs.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.updateWorkout = this.updateWorkout.bind(this);
    this.forceSessionsUpdate = this.forceSessionsUpdate.bind(this);
  }

  handleDrop({ operation }) {
    if (!operation.target) return;

    const sourceId = operation.source.data.id;
    const targetId = operation.target.data.id;

    if (sourceId === targetId) return;

    this.forceSessionsUpdate(sourceId, targetId);
    this.updateWorkout(sourceId, targetId);
  }

  updateWorkout(dateFrom, dateTo) {
    const athlete =
      _.get(this.locals, "query.athlete") || _.get(this.locals, "athlete._id");

    submit(
      `/actions/exercise/move-day?proxyAthlete=${athlete}&reqSource=calendar&dnd=true`,
      {
        method: "POST",
        body: {
          from: moment.utc(dateFrom, DATE_DAY_FORMAT).startOf("day").toJSON(),
          to: moment(dateTo, DATE_DAY_FORMAT).startOf("day").toJSON(),
          type: "move",
        },
      }
    );
  }

  forceSessionsUpdate(draggingFrom, draggingTo) {
    const { scheduledSessions } = this.state;

    const tempSessions = { ...scheduledSessions };
    const oldDate = tempSessions[draggingFrom];

    delete tempSessions[draggingFrom];
    tempSessions[draggingTo] = oldDate;

    this.setState({
      scheduledSessions: tempSessions,
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps, nextContext) {
    const { locals } = nextContext;
    const { scheduledSessions } = locals;
    if (scheduledSessions !== this.state.scheduledSessions) {
      this.setState({ scheduledSessions });
    }
  }

  goToMyPrograms(e) {
    e.preventDefault();

    setTimeout(
      () =>
        submit("/dashboard/program-builder", {
          method: "GET",
        }),
      300
    );
  }

  goToContactUs(e) {
    e.preventDefault();

    setTimeout(
      () =>
        submit("/dashboard/support/contact", {
          method: "GET",
        }),
      300
    );
  }

  goToExerciseLibrary(e) {
    e.preventDefault();
    this.closeAthleteWelcomeForCoach();
    setTimeout(
      () =>
        submit("/dashboard/settings/exercise-library", {
          method: "GET",
        }),
      300
    );
  }

  componentDidMount() {
    const { activeUserToRequest } = this.context.locals;

    // the property `welcomeVideoShown` was moved into `welcomeVideos`. But some users still have this property
    // out of this new object. So we need to check both
    if (
      activeUserToRequest.pushAccountInfo &&
      !activeUserToRequest.pushAccountInfo.accountLinkFinished
    ) {
      this.setState({ showAccountLinkVideo: true });
    }
  }

  handleSelectDate(e, dayString) {
    const selectedDates = this.state.selectedDates;

    if (selectedDates.indexOf(dayString) > -1) {
      selectedDates.splice(selectedDates.indexOf(dayString), 1);
    } else {
      selectedDates.push(dayString);
    }

    this.setState({ selectedDates, showFooter: Boolean(selectedDates.length) });
  }

  handleRemoveMultiple(e, singleDate = false) {
    if (confirm(this.props.t("calendar.calendarMonth.removeSelectedConfirm"))) {
      e.preventDefault();

      const days = this.state.selectedDates.length
        ? this.state.selectedDates.map((date) =>
            moment(date).format(DATE_DAY_FORMAT)
          )
        : [moment(singleDate).format(DATE_DAY_FORMAT)];

      submit(`/actions/exercise/unschedule-workout?days=${days}`, {
        method: "POST",
        body: { days },
      });

      this.setState({
        selectedDates: [],
        showFooter: false,
      });
    }
  }

  handleDeselectAll() {
    this.setState({
      selectedDates: [],
    });
  }

  handleSelectAll() {
    const { scheduledSessions } = this.context.locals;
    const newSelectedDates = [];
    Object.keys(scheduledSessions).map((key) => {
      newSelectedDates.push(key);
    });

    this.setState({
      selectedDates: newSelectedDates,
    });
  }

  switchMonth(e, direction, activeMonth) {
    e.preventDefault();
    this.setState({ loaded: false, selectedDates: [], showFooter: false });
    submit(`/actions/refresh?`, {
      method: "GET",
      body: {
        month: activeMonth
          .clone()
          .add(direction, "months")
          .format(DATE_YEAR_FORMAT),
      },
    });
  }

  render() {
    const {
      activeMonth,
      query,
      startOfFirstWeek,
      scheduledSessions,
      activeExercises,
      activeUser,
      activeUserToRequest,
      fromMetric,
      organizationOwner,
      clientConfiguration,
    } = this.context.locals;
    const { isCoachEditing, startingCalendarDay } = activeUser;

    const calendarDay = startingCalendarDay || 0;
    const monthInsightsRange = getCalendarMonthInsightsRange(
      activeMonth,
      calendarDay
    );
    const insightsPeriodKeys = monthInsightsRange.visiblePeriods
      .map((period) => period.periodKey)
      .join("|");
    const athleteId =
      _.get(this.context.locals, "athlete._id") || activeUser._id;

    const { selectedDates } = this.state;

    const totalScheduledSessions = Object.keys(scheduledSessions).length;
    const allSelected = totalScheduledSessions === selectedDates.length;
    return (
      <main className="Calendar">
        <header>
          <section>
            <hgroup>
              {selectedDates.length > 0 ? (
                <>
                  {totalScheduledSessions > 0 && (
                    <button
                      data-button-type="secondary"
                      onClick={
                        allSelected
                          ? this.handleDeselectAll
                          : this.handleSelectAll
                      }
                    >
                      <SvgIcon
                        className="mr-2"
                        src={
                          allSelected
                            ? "/img/new-icons/checked-square.svg"
                            : "/img/new-icons/unchecked-square.svg"
                        }
                        noFilterOnLight={allSelected}
                      />
                      {allSelected
                        ? this.props.t("calendar.calendarMonth.deselectAll")
                        : this.props.t("calendar.calendarMonth.selectAll")}
                    </button>
                  )}
                  <p className="p4">
                    ({selectedDates.length}{" "}
                    {this.props.t("calendar.calendarMonth.selected")})
                  </p>
                </>
              ) : (
                <>
                  <a
                    href={`/actions/refresh?month=${moment().format(
                      DATE_YEAR_FORMAT
                    )}`}
                    role="button"
                    data-button-type="outline"
                  >
                    <span>
                      {this.props.t("calendar.calendarMonth.thisMonth")}
                    </span>
                  </a>
                  <div
                    className="expand"
                    onClick={() =>
                      this.setState({ expanded: !this.state.expanded })
                    }
                  >
                    <span>
                      {this.props.t("calendar.calendarMonth.expandCalendar")}
                    </span>
                    {
                      <SvgIcon
                        src={`/img/new-icons/${
                          this.state.expanded ? "" : "un"
                        }checked-square.svg`}
                        className="icon-white"
                        alt="expand"
                        noFilterOnLight={this.state.expanded}
                      />
                    }
                  </div>
                </>
              )}
            </hgroup>
            <hgroup>
              <div role="month-handler">
                <div>
                  <button
                    data-button-type="text-white"
                    data-icon-only
                    onClick={(e) => this.switchMonth(e, -1, activeMonth)}
                  >
                    <SvgIcon src="/img/new-icons/arrow-left.svg" alt="prev" />
                  </button>
                  <span className="label">
                    {activeMonth.format("MMMM, YYYY")}
                  </span>
                  <button
                    data-button-type="text-white"
                    data-icon-only
                    onClick={(e) => this.switchMonth(e, 1, activeMonth)}
                  >
                    <SvgIcon src="/img/new-icons/arrow-right.svg" alt="next" />
                  </button>
                </div>
              </div>
            </hgroup>
            <hgroup>
              {selectedDates.length > 0 ? (
                <>
                  <button
                    data-button-type="secondary"
                    onClick={() => this.setState({ selectedDates: [] })}
                  >
                    {this.props.t("calendar.calendarMonth.cancel")}
                  </button>
                  <button
                    data-button-type="secondary"
                    className="text-(--danger)!"
                    onClick={this.handleRemoveMultiple}
                    disabled={selectedDates.length === 0}
                  >
                    <SvgIcon
                      className="mr-2"
                      src={"/img/new-icons/red-trash.svg"}
                      noFilterOnLight={true}
                    />
                    {this.props.t("calendar.calendarMonth.remove")}
                  </button>
                </>
              ) : (
                <ToggleLinksInput
                  name="view"
                  className="view-segmented"
                  options={[
                    {
                      text: this.props.t("calendar.topBarSingleDay.month"),
                      value: "month",
                      href: `/actions/refresh?view=month&month=${
                        query.week
                          ? moment(query.week, DATE_DAY_FORMAT).format(
                              DATE_YEAR_FORMAT
                            )
                          : query.date
                            ? moment(query.date, DATE_DAY_FORMAT).format(
                                DATE_YEAR_FORMAT
                              )
                            : ""
                      }&week=&date=&relTrends=true`,
                    },
                    {
                      text: this.props.t("calendar.topBarSingleDay.week"),
                      value: "week",
                      href: `/actions/refresh?view=week&week=${
                        moment().isSame(query.month, "month")
                          ? moment().format(DATE_DAY_FORMAT)
                          : moment(query.month, DATE_YEAR_FORMAT).format(
                              DATE_DAY_FORMAT
                            )
                      }&month=&date=&relTrends=true`,
                    },
                    {
                      text: this.props.t("calendar.topBarSingleDay.day"),
                      value: "day",
                      href: `/actions/refresh?view=day&date=${
                        moment().isSame(query.month, "month")
                          ? moment().format(DATE_DAY_FORMAT)
                          : moment(query.month, DATE_YEAR_FORMAT).format(
                              DATE_DAY_FORMAT
                            )
                      }&month=&week=&relTrends=true`,
                    },
                  ]}
                />
              )}
            </hgroup>
          </section>
        </header>

        <DragDropProvider onDragEnd={this.handleDrop}>
          <CalendarInsightsSection
            scope="calendar-month"
            enabled
            from={monthInsightsRange.from}
            to={monthInsightsRange.to}
            periodKeys={insightsPeriodKeys}
            visiblePeriods={monthInsightsRange.visiblePeriods}
            activeExercises={activeExercises}
            athleteId={athleteId}
            startingCalendarDay={calendarDay}
            activeUser={activeUser}
            activeUserToRequest={activeUserToRequest}
            organizationOwner={organizationOwner}
            clientConfiguration={clientConfiguration}
            fromMetric={fromMetric}
          >
            {(monthGridScrollRef) => (
              <MonthGrid ref={monthGridScrollRef} startDay={calendarDay}>
                {weeksInMonth.map((week) => {
                  const startOfWeek = startOfFirstWeek
                    .clone()
                    .add(week, "weeks");
                  if (
                    startOfWeek.month() === activeMonth.clone().month() ||
                    startOfWeek.clone().endOf("week").month() ===
                      activeMonth.clone().month()
                  ) {
                    return (
                      <MonthWeek
                        activeMonth={activeMonth.clone()}
                        key={week}
                        startOfWeek={startOfWeek}
                        handleSelectDate={this.handleSelectDate}
                        selectedDates={this.state.selectedDates}
                        scheduledSessions={this.state.scheduledSessions}
                        handleRemoveMultiple={this.handleRemoveMultiple}
                        expanded={this.state.expanded}
                      />
                    );
                  }
                })}
              </MonthGrid>
            )}
          </CalendarInsightsSection>
        </DragDropProvider>

        {isCoachEditing && (
          <div className="Coach-Edit">
            <img src="/img/icons/edit.svg" width="20" height="20" alt="" />
            {this.props.t("calendar.calendarMonth.coachEditingMessage")}
          </div>
        )}

        <input type="radio" name="more" id="more-reset" />
      </main>
    );
  }
}

View.contextType = RillContext;

const CalendarMonthView = withTranslation("dashboard")(View);

const CalendarMonth = (app) => {
  app.get("/", async ({ req, res, locals, load }) => {
    // there is no mobile view for the month calendar view so if we are on mobile
    // redirect to the week view whcih does have mobile styles.
    //     if (
    //       process.browser &&
    //       window.innerWidth &&
    //       Number(window.innerWidth) < 960
    //     ) {
    //       res.redirect("/dashboard/calendar?view=week");
    //     }

    const { query } = req;
    _.defaults(query, {
      month: moment().format(DATE_YEAR_FORMAT),
      view: "month",
    });
    const modal = _.get(query, "modal", {});

    locals.activeMonth = moment(query.month, DATE_YEAR_FORMAT);
    const { startOfFirstWeek, endOfLastWeek } = getCalendarMonthGridBounds(
      locals.activeMonth,
      locals.activeUser.startingCalendarDay || 0
    );
    locals.startOfFirstWeek = startOfFirstWeek;
    locals.endOfLastWeek = endOfLastWeek;
    // Cache remove for MSB-411
    const isCurrentMonth = locals.activeMonth.isSame(moment(), "month");

    await Promise.all([
      load("activeExercises", {
        start: locals.startOfFirstWeek,
        end: locals.endOfLastWeek,
      }),
      load("dayPersonalRecords", {
        start: locals.startOfFirstWeek.clone().startOf("day").format(UTC),
        end: isCurrentMonth
          ? moment().startOf("day").format(UTC)
          : locals.endOfLastWeek.clone().endOf("day").format(UTC),
        isCheckIn: true,
      }),
    ]);
    locals.scheduledSessions = _.groupBy(locals.activeExercises, (e) => {
      return moment
        .utc((e.utcDate && e.utcDate.toString()) || e.date)
        .format(DATE_DAY_FORMAT);
    });
    const cycles = _.uniqBy(
      locals.activeExercises.filter((e) => e.cycle),
      "cycle.id"
    ).map((exercise) => exercise.cycle.id);
    locals.cycle = await load("cycles", cycles);

    locals.modal = {
      "calendar-add-exercise": <CalendarAddExerciseModal />,
      "calendar-duplicate-workout": <CalendarDuplicateWorkoutModal />,
      "calendar-move-workout": <CalendarMoveWorkoutModal locals={locals} />,
      "calendar-shift-workout": <CalendarShiftWorkoutModal />,
      video: <VideoModal />,
    }[modal.type];

    if (
      modal.type === "calendar-move-workout" ||
      modal.type === "calendar-duplicate-workout" ||
      modal.type === "calendar-shift-workout"
    ) {
      await load("activeExercises", {
        start: moment(0),
        end: moment("2049-12-31"),
      });
    }

    // first login
    if (query.firstLogin && !locals.activeUser.coach) {
      delete query.firstLogin;
      return res.redirect(`/dashboard/calendar?modal[type]=video`);
    }

    res.body = <CalendarMonthView />;
  });
};

export default CalendarMonth;
